import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  DEFAULT_CATEGORY_ID,
  type BlueprintSection,
  type Language,
  type LocalizedLabel,
} from '../../../types';
import { useDefinitionContext } from '../../contexts/DefinitionContext';
import { useSortingEditor } from '../../hooks/useSortingEditor';

interface SortingEditorProps {
  blueprintSection: BlueprintSection;
  sectionId: string;
}

function resolveLabel(
  label: LocalizedLabel | undefined,
  language: Language,
  fallback: string,
): string {
  if (!label) return fallback;
  return label[language] || fallback;
}

export function SortingEditor({ blueprintSection, sectionId }: SortingEditorProps) {
  const { languages } = useDefinitionContext();
  const { definitionSection, handleAddSortRule, handleRemoveSortRule, handleUpdateSortRule } =
    useSortingEditor({ sectionId });

  const sortRules = definitionSection?.sorting || [];
  const primaryLanguage = languages[0] || 'en';

  // Get IDs of selected columns
  const selectedColumnIds = new Set(definitionSection?.columns?.map((col) => col.id) || []);

  // Filter to only include sortable columns that are actually selected
  const sortableColumns = blueprintSection.columns.filter(
    (col) => col.isSortable && selectedColumnIds.has(col.id),
  );

  // Filter out orphaned sort rules (rules referencing columns that are no longer selected)
  const validSortRules = sortRules.filter((rule) => selectedColumnIds.has(rule.id));

  // Maximum number of sort rules is limited by the number of sortable columns
  const maxSortRules = sortableColumns.length;

  const handleAddSort = () => {
    if (validSortRules.length >= maxSortRules) {
      return; // Maximum sort fields limited by available sortable columns
    }
    if (sortableColumns.length > 0) {
      const firstColumn = sortableColumns[0];
      handleAddSortRule(firstColumn.id, 'asc');
    }
  };

  // Helper to find the original index in sortRules array based on display index
  // Since validSortRules is a filtered subset, we need to map display index to original index
  const findOriginalIndex = (displayIndex: number): number => {
    // Count how many valid rules we've seen so far
    let validCount = 0;
    for (let i = 0; i < sortRules.length; i++) {
      if (selectedColumnIds.has(sortRules[i].id)) {
        if (validCount === displayIndex) {
          return i;
        }
        validCount++;
      }
    }
    return -1;
  };

  const handleColumnChange = (index: number, columnId: string) => {
    // Update rule in place instead of remove+add
    const oldRule = validSortRules[index];
    if (oldRule) {
      const originalIndex = findOriginalIndex(index);
      if (originalIndex !== -1) {
        handleUpdateSortRule(originalIndex, columnId, oldRule.direction);
      }
    }
  };

  const handleDirectionChange = (index: number, direction: 'asc' | 'desc') => {
    const rule = validSortRules[index];
    if (rule) {
      const originalIndex = findOriginalIndex(index);
      if (originalIndex !== -1) {
        handleUpdateSortRule(originalIndex, rule.id, direction);
      }
    }
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h6">
          <FormattedMessage id="audit.reportDefinitions.sorting.order" defaultMessage="Orden" />
        </Typography>
      </Box>

      {validSortRules.length === 0 ? (
        <Typography variant="body2" color="text.secondary" mb={2}>
          <FormattedMessage
            id="audit.reportDefinitions.sorting.noRules"
            defaultMessage={'No hay reglas de orden. Haz clic en "Agregar orden" para agregar una.'}
          />
        </Typography>
      ) : (
        <Box>
          {validSortRules.map((rule, index) => {
            return (
              <Box
                key={`${rule.id}-${index}`}
                display="flex"
                gap={2}
                alignItems="center"
                marginBottom={2}
              >
                <Box sx={{ minWidth: 16, textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary">
                    {index + 1}.
                  </Typography>
                </Box>
                <FormControl size="small" sx={{ width: 300 }}>
                  <Select
                    value={rule.id}
                    onChange={(e) => {
                      handleColumnChange(index, e.target.value);
                    }}
                  >
                    {sortableColumns.map((col) => {
                      const columnLabel = resolveLabel(
                        col.defaultLabel as LocalizedLabel,
                        primaryLanguage,
                        col.key,
                      );
                      const categoryText =
                        (col.category || DEFAULT_CATEGORY_ID) !== DEFAULT_CATEGORY_ID
                          ? ` (${col.category})`
                          : '';
                      return (
                        <MenuItem key={col.id} value={col.id}>
                          {columnLabel}
                          {categoryText}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: 200 }}>
                  <Select
                    value={rule.direction}
                    onChange={(e) => handleDirectionChange(index, e.target.value as 'asc' | 'desc')}
                  >
                    <MenuItem value="asc">
                      <FormattedMessage id="audit.sorting.ascending" defaultMessage="Ascendente" />
                    </MenuItem>
                    <MenuItem value="desc">
                      <FormattedMessage
                        id="audit.sorting.descending"
                        defaultMessage="Descendente"
                      />
                    </MenuItem>
                  </Select>
                </FormControl>

                {validSortRules.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      const originalIndex = findOriginalIndex(index);
                      if (originalIndex !== -1) {
                        handleRemoveSortRule(originalIndex);
                      }
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddSort}
          disabled={sortableColumns.length === 0 || validSortRules.length >= maxSortRules}
        >
          <FormattedMessage id="audit.reports.sorting.add" defaultMessage="Agregar" />
        </Button>
      </Box>
    </Paper>
  );
}
